import { type VariantProps } from 'class-variance-authority';
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui';
import { type ComponentPropsWithoutRef, type FC } from 'react';
import { type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import { solinkButtonVariants } from './Button';
import { DARK_OVERLAY_SURFACE_CLASSES } from './DarkThemeScope';

import {
  AlertDialogContent as ShadcnAlertDialogContent,
  AlertDialogDescription as ShadcnAlertDialogDescription,
  AlertDialogFooter as ShadcnAlertDialogFooter,
  AlertDialogHeader as ShadcnAlertDialogHeader,
  AlertDialogTitle as ShadcnAlertDialogTitle,
} from './ui/alert-dialog';
import { Button as ShadcnButton } from './ui/button';

export type AlertDialogContentProps = ComponentPropsWithoutRef<typeof ShadcnAlertDialogContent> & {
  /** Light (default) or dark surface. Pass `"dark"` for an alert dialog opened from inside a `DarkThemeScope` — it portals to `document.body` and so escapes the scope's own `.dark`. */
  variant?: PortaledSurfaceVariant;
};
export const AlertDialogContent: FC<AlertDialogContentProps> = ({
  variant = 'light',
  ...props
}) => {
  // `tw` because AlertDialogContent portals onto `body`, outside the app's own `.tw`-scoped
  // Tailwind subtree — without it, this loses the Tailwind preflight reset entirely (including
  // the design system's base font-family), falling back to the browser/OS default sans-serif
  // (Roboto/Arial). Dialog's own DialogContent and Popover already carry this same fix.
  //
  // `text-foreground-primary` unconditional (not `dark:`-prefixed) so any plain/unstyled
  // content dropped directly in AlertDialogContent tracks `--foreground-primary` via ordinary
  // CSS custom property inheritance, the same way Dialog's DialogContent does.
  //
  // Border color + shadow mirror Dialog's own `dialogContentVariants` exactly (Figma "Dialog"
  // 6996:1133) — the raw shadcn defaults (`border`, `shadow-lg`) don't match that spec.
  //
  // The scrim (AlertDialogOverlay) isn't touched here — unlike DialogContent, the generated
  // AlertDialogContent has no seam to reach it, and its `bg-black/50` is already a fixed,
  // theme-invariant color that doesn't need a dark variant.
  return (
    <ShadcnAlertDialogContent
      {...props}
      className={cn(
        'tw border-border-muted text-foreground-primary shadow-[0px_10px_10px_-5px_rgba(0,64,76,0.05),0px_2px_25px_-5px_rgba(0,64,76,0.1)]',
        variant === 'dark' && DARK_OVERLAY_SURFACE_CLASSES,
        props.className,
      )}
    />
  );
};

export type AlertDialogTitleProps = ComponentPropsWithoutRef<typeof ShadcnAlertDialogTitle>;
export const AlertDialogTitle: FC<AlertDialogTitleProps> = (props) => {
  // Matches Dialog's DialogTitle exactly (`heading-lg text-foreground-primary`) — the raw
  // shadcn title sets size/weight only, no font-family or color.
  return (
    <ShadcnAlertDialogTitle
      {...props}
      className={cn('text-foreground-primary heading-lg', props.className)}
    />
  );
};

export type AlertDialogDescriptionProps = ComponentPropsWithoutRef<
  typeof ShadcnAlertDialogDescription
>;
export const AlertDialogDescription: FC<AlertDialogDescriptionProps> = (props) => {
  // Matches Dialog's DialogDescription (`text-foreground-secondary body-sm`): a compact
  // subheading that sits under the title, for use ALONGSIDE body content — not as the only
  // content. When the whole message is a single line, put that line in the body (between the
  // header and footer separators) and keep an `sr-only` AlertDialogDescription for the
  // accessible name, rather than showing it as a bare subheading in the header.
  return (
    <ShadcnAlertDialogDescription
      {...props}
      className={cn('text-foreground-secondary body-sm', props.className)}
    />
  );
};

export type AlertDialogHeaderProps = ComponentPropsWithoutRef<typeof ShadcnAlertDialogHeader>;
export const AlertDialogHeader: FC<AlertDialogHeaderProps> = (props) => {
  // Matches Dialog's DialogHeader separator: bleeds to AlertDialogContent's own `p-6` edges
  // via negative margins so the border sits flush with the dialog's sides. Only adds the
  // border/spacing — the raw component's own grid/media layout classes are left untouched.
  return (
    <ShadcnAlertDialogHeader
      {...props}
      className={cn('-mx-6 -mt-6 mb-2 border-b border-border-muted px-6 py-4', props.className)}
    />
  );
};

export type AlertDialogFooterProps = ComponentPropsWithoutRef<typeof ShadcnAlertDialogFooter>;
export const AlertDialogFooter: FC<AlertDialogFooterProps> = (props) => {
  // Matches Dialog's DialogFooter separator — same bleed technique as the header above.
  return (
    <ShadcnAlertDialogFooter
      {...props}
      className={cn('-mx-6 -mb-6 mt-2 border-t border-border-muted px-6 py-4', props.className)}
    />
  );
};

export type AlertDialogActionProps = ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> &
  Pick<VariantProps<typeof solinkButtonVariants>, 'variant' | 'size'>;
export const AlertDialogAction: FC<AlertDialogActionProps> = ({
  variant = 'default',
  size = 'default',
  className,
  ...props
}) => (
  // The raw shadcn AlertDialogAction/Cancel compose with `ui/button`'s RAW Button, not
  // solink-ui's own `Button` — so these render with the un-styled shadcn defaults (size,
  // radius, typography) instead of matching every other button in the app. Composing here
  // with the same raw Button + `asChild` mechanism (proven safe for Radix's Slot-based
  // polymorphism) but applying `solinkButtonVariants` gets the identical visual result as
  // solink-ui's own `<Button>` without solink-ui's Button's extra loading-spinner markup,
  // which isn't compatible with `asChild` (it always wraps children in its own `<span>`s).
  // The generated button has no `video` variant. `ghost` adds no fill, so the solink
  // `video` classes own the surface.
  <ShadcnButton
    variant={variant === 'video' ? 'ghost' : variant}
    size={size}
    asChild
    className={cn(solinkButtonVariants({ variant, size }), className)}
  >
    <AlertDialogPrimitive.Action data-slot="alert-dialog-action" {...props} />
  </ShadcnButton>
);

export type AlertDialogCancelProps = ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> &
  Pick<VariantProps<typeof solinkButtonVariants>, 'variant' | 'size'>;
export const AlertDialogCancel: FC<AlertDialogCancelProps> = ({
  variant = 'outline',
  size = 'default',
  className,
  ...props
}) => (
  <ShadcnButton
    variant={variant === 'video' ? 'ghost' : variant}
    size={size}
    asChild
    className={cn(solinkButtonVariants({ variant, size }), className)}
  >
    <AlertDialogPrimitive.Cancel data-slot="alert-dialog-cancel" {...props} />
  </ShadcnButton>
);

export {
  AlertDialog,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTrigger,
} from './ui/alert-dialog';
